import React, { useEffect, useState } from "react";
import { ChevronLeft } from "../icons/chevron-left";
import { ChevronRight } from "../icons/chevron-right";
import { cn } from "../utls/utils";
import { Modal } from "../shared/modal";

interface Image {
  url: string;
  fileName?: string;
}

interface ImageModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageSrc: string;
  imageAlt?: string;
  images?: Image[];  // Optional array of all images for navigation
  currentIndex?: number;  // Current image index if part of a set
}

export function ImageModal({
  isOpen,
  onClose,
  imageSrc,
  imageAlt = "Image preview",
  images = [],
  currentIndex = 0,
}: ImageModalProps) {
  const [activeIndex, setActiveIndex] = useState(currentIndex);
  const hasMultipleImages = images.length > 1;
  
  // Use the current imageSrc if no images array provided
  const currentImage = hasMultipleImages 
    ? images[activeIndex] 
    : { url: imageSrc, fileName: imageAlt };
  
  // Reset active index when modal opens or images change
  useEffect(() => {
    if (isOpen) {
      setActiveIndex(currentIndex);
    }
  }, [isOpen, currentIndex, images]);
  
  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (hasMultipleImages) {
        if (event.key === "ArrowLeft") {
          navigatePrevious();
        } else if (event.key === "ArrowRight") {
          navigateNext();
        }
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, activeIndex, hasMultipleImages]);
  
  const navigatePrevious = () => {
    if (hasMultipleImages && activeIndex > 0) {
      setActiveIndex(activeIndex - 1);
    }
  };
  
  const navigateNext = () => {
    if (hasMultipleImages && activeIndex < images.length - 1) {
      setActiveIndex(activeIndex + 1);
    }
  };

  return (
    <Modal 
      isOpen={isOpen}
      onClose={onClose}
    >
      <div className="relative max-w-[90vw] max-h-[90vh]">
        {/* Previous image button */}
        {hasMultipleImages && (
          <button
            className={cn(
              "absolute left-4 top-1/2 transform -translate-y-1/2 p-2 rounded-full",
              "bg-terracotta-500/75 text-white hover:bg-terracotta-600/90 transition-colors",
              activeIndex === 0 && "opacity-50 cursor-not-allowed"
            )}
            onClick={navigatePrevious}
            disabled={activeIndex === 0}
            aria-label="Previous image"
          >
            <ChevronLeft active={true} />
          </button>
        )}
        
        {/* Current image */}
        <img
          src={currentImage.url}
          alt={currentImage.fileName || imageAlt}
          className={cn(
            "max-w-full max-h-[85vh] object-contain",
            "rounded-md shadow-xl"
          )}
        />
        
        {/* Next image button */}
        {hasMultipleImages && (
          <button
            className={cn(
              "absolute right-4 top-1/2 transform -translate-y-1/2 p-2 rounded-full",
              "bg-terracotta-500/75 text-white hover:bg-terracotta-600/90 transition-colors",
              activeIndex === images.length - 1 && "opacity-50 cursor-not-allowed"
            )}
            onClick={navigateNext}
            disabled={activeIndex === images.length - 1}
            aria-label="Next image"
          >
            <ChevronRight active={true} />
          </button>
        )}
        
        {/* Image counter */}
        {hasMultipleImages && (
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-terracotta-500/75 text-white px-3 py-1 rounded-full">
            {activeIndex + 1} / {images.length}
          </div>
        )}
      </div>
    </Modal>
  );
}